<script lang="ts">
  import { SEARCH_PROGRESS, SUBSCRIPTION_TIER } from "$lib/types";
  import type { Search, SearchOptions } from "$lib/types";
  import { searchStore, searchHistoryStore, authStore, searchPreferences, userOnboardingStore } from "$lib/stores";
  import { get } from "svelte/store";
  import { goto } from "$app/navigation";
  import Switch from "$lib/components/basic/switch/switch.svelte";
  import { onMount, onDestroy } from "svelte";
  import { browser } from "$app/environment";
  import { pills } from "$lib/data/searchSuggestions";
  import { cn } from "$lib/utils";
  import { searchIcon, arrowUpIcon } from "$lib/images/icons";
  import routes from "$lib/routes";
  import { SVG } from "$lib/components";
  import { CornerDownRight } from "lucide-svelte";
  import { searchFormOverlayStore } from "$lib/stores/searchFormOverlayStore";

  import SubscriptionDialog from "$lib/components/dialogs/subscriptionDialog.svelte";
  let showDialog = $state(false);

  import { createEventDispatcher } from "svelte";
  const dispatch = createEventDispatcher();

  type $$Events = {
    searchSubmitted: CustomEvent<{ query: string }>;
  };

  let {
    showSuggestions = false,
    placeholder = "Type to find",
    inputClassName = "placeholder:font-light",
    searchContainerClass = "",
    containerClass = "",
    inputWrapperClass = "",
    formClass = "",
    useOverlayStore = false,
    queryId = "searchQuery",
    searchQueryInputValue = $bindable<string>(""),
    showSendButton = true,
    autofocus = true,
  } = $props();

  let activePill = $state<string | null>(null);
  let searchContainer = $state<HTMLElement | undefined>();
  let currentSuggestions = $state<string[]>([]);
  let isShowingSuggestions = $state(false);
  let lastSearchTime = $state<number>(0);
  let isSearchDisabled = $state(false);

  let searchesRemainingStatus = $derived(`${$authStore.user?.proModeRemaining} Pro searches remaining`);
  let showStatusBar = $derived(
    $authStore.isLoggedIn && $authStore.user?.plan === SUBSCRIPTION_TIER.PLUS && $searchPreferences.proMode,
  );

  const SEARCH_COOLDOWN = 4000;

  async function handleSearch(query: string, options?: SearchOptions) {
    const currentTime = Date.now();
    if (currentTime - lastSearchTime < SEARCH_COOLDOWN) {
      return;
    }

    let customCriteria: string[] = [];
    if (useOverlayStore) {
      // searchPreferences.setProModeValue({value: $searchFormOverlayStore.proMode});
      customCriteria = $searchFormOverlayStore.criteria;
    }

    if (
      $searchPreferences.proMode &&
      $authStore.user?.proModeRemaining === 0 &&
      $authStore.user?.proModeAccess !== "unlimited"
    ) {
      showDialog = true;
      return;
    }

    try {
      isSearchDisabled = true;
      lastSearchTime = currentTime;

      const searchOptions: SearchOptions = {
        mode: $searchPreferences.proMode ? "pro" : "default",
        customCriteria: customCriteria,
        findAll: $searchPreferences.useFindAll,
      };

      const searchParams = createSearchParams(query, searchOptions);

      await initiateSearch(searchParams, searchOptions, { skipEvents: true });

      setTimeout(() => {
        isSearchDisabled = false;
      }, SEARCH_COOLDOWN);

      return searchParams.slug;
    } catch (error) {
      console.error("Error creating search:", error);
      isSearchDisabled = false;
      throw new Error("Failed to create search");
    }
  }

  function createSearchParams(query: string, options?: SearchOptions): Search {
    const userID = get(authStore).user?.id;

    let params: Search = {
      userID,
      mode: $authStore.isLoggedIn ? options?.mode : "default",
      query: query.trim(),
      slug: searchStore.createSlug(query),
      matchesCount: 0,
      results: [],
      createdAt: new Date(),
      progressStatus: SEARCH_PROGRESS.NOT_STARTED,
      isPublic: false,
    };

    return params;
  }

  async function initiateSearch(
    searchParams: Search,
    options?: SearchOptions,
    custom?: { skipEvents?: boolean },
  ): Promise<void> {
    if (!$authStore.isLoggedIn) {
      options = { ...options, mode: "default" };
    }
    searchHistoryStore.addSearch(searchParams);
    if (options?.redirectAfterCreate !== false) {
      const currentPath = window.location.pathname;
      if (currentPath.startsWith("/search-dev")) {
        await goto(`/search-dev/${searchParams.slug}`);
      } else {
        await goto(`/search/${searchParams.slug}`);
      }
    }
    logger.log("Performing search with events");
    logger.log("Search params:", searchParams);
    logger.log("options:", options);
    if (custom && custom.skipEvents) {
      await searchStore.performSearchWithoutEvents(
        {
          query: searchParams.query,
          criteria: options?.customCriteria,
          initialSlug: searchParams.slug,
          userID: searchParams.userID,
          findAll: options?.findAll,
        },
        options,
      );
    } else {
      await searchStore.performSearch(
        {
          query: searchParams.query,
          criteria: options?.customCriteria,
          initialSlug: searchParams.slug,
          userID: searchParams.userID,
          findAll: options?.findAll,
        },
        options,
      );
    }
  }

  export function focusInput() {
    if (!browser) return;

    const input = document.getElementById(queryId) as HTMLInputElement;
    if (input) {
      input.focus();
      input.setSelectionRange((searchQueryInputValue as string).length, (searchQueryInputValue as string).length);
    }
  }

  function submitSearchForm(event?: Event) {
    event?.preventDefault();
    // logger.log('submitSearchForm with searchQueryInputValue:', searchQueryInputValue);
    if (searchQueryInputValue && !isSearchDisabled) {
      // logger.log('dispatched searchSubmitted event');
      // logger.log('searchQueryInputValue:', searchQueryInputValue);
      handleSearch(searchQueryInputValue as string);
      dispatch("searchSubmitted", { query: searchQueryInputValue });
    }
  }

  function handleSuggestionClick(suggestion: string) {
    if (!isSearchDisabled) {
      handleSearch(suggestion);
    }
  }

  function handleKeydown(event: KeyboardEvent) {
    if (!browser) return;

    const input = document.getElementById(queryId) as HTMLInputElement;
    if (input && document.activeElement === input) {
      if ((event.ctrlKey || event.metaKey) && event.key === "Enter") {
        event.preventDefault();
        submitSearchForm();
      }
    }
  }

  function handlePillClick(pill: { text: string; suggestions: string[]; keyword: string }) {
    if (activePill === pill.text) {
      activePill = null;
      currentSuggestions = [];
      isShowingSuggestions = false;
    } else {
      activePill = pill.text;
      currentSuggestions = pill.suggestions;
      isShowingSuggestions = true;

      searchContainer?.dispatchEvent(
        new CustomEvent("pillSelect", {
          detail: {
            pillKeyword: pill.keyword,
            isActive: activePill !== pill.text,
          },
          bubbles: true,
        }),
      );
    }
  }

  function handleClickOutside(event: MouseEvent) {
    if (searchContainer && !searchContainer.contains(event.target as Node)) {
      if (activePill) {
        activePill = null;
        currentSuggestions = [];
        isShowingSuggestions = false;

        searchContainer.dispatchEvent(
          new CustomEvent("pillDeselect", {
            detail: {},
            bubbles: true,
          }),
        );
      }
    }
  }

  onMount(async () => {
    if (!browser) return;
    authStore.updateProModeAccess();
    searchPreferences.setProModeValue({ default: true });
    if (!authStore.isAdmin()) {
      if ($searchPreferences.proMode) {
        searchPreferences.updatePreference("searchAlgoVersion", "v3.5");
      }
    }

    document.addEventListener("keydown", handleKeydown);
    document.addEventListener("click", handleClickOutside);
  });

  onDestroy(() => {
    if (!browser) return;

    document.removeEventListener("keydown", handleKeydown);
    document.removeEventListener("click", handleClickOutside);
  });
</script>

<div class={cn("z-10", containerClass)}>
  <div class={cn("mx-auto w-full max-w-7xl px-4", searchContainerClass)} bind:this={searchContainer}>
    <form id="search-form" onsubmit={submitSearchForm} class={formClass}>
      <div class="relative mx-auto max-w-lg">
        <div class={cn("rounded-full text-theme-text-secondary md:mt-0")}>
          <div
            class={cn(
              "flex w-full max-w-full items-center gap-2 rounded-full bg-black pl-4 pr-5 pt-0 dark:bg-white/10",
              inputWrapperClass,
            )}
          >
            <div class="text-white dark:text-white/60">
              <SVG file={searchIcon} className="max-w-6" />
            </div>

            <!-- Input -->
            <!-- svelte-ignore a11y_autofocus -->
            <input
              id={queryId}
              type="text"
              {autofocus}
              autocomplete="off"
              bind:value={searchQueryInputValue}
              {placeholder}
              class={cn(
                "ph-unmasked appearance-none",
                "bg-black dark:bg-transparent",
                "text-white dark:text-white",
                "placeholder:text-white/60 dark:placeholder:text-white/40",
                "w-full border-none focus:outline-none",
                "h-12 text-base md:h-10 md:text-sm",
                "font-normal focus:ring-0",
                inputClassName,
              )}
            />

            <Switch
              label="Pro"
              labelPosition="right"
              highlight={true}
              containerClass="text-white dark:text-black text text-sm md:text-xs"
              bind:value={$searchPreferences.proMode}
              canToggle={$authStore.user?.plan === SUBSCRIPTION_TIER.PLUS ||
                $authStore.user?.plan === SUBSCRIPTION_TIER.PRO ||
                false}
              toggleGuard={() => {
                searchPreferences.updatePreference("proMode", false);
                if (!authStore.isAdmin()) {
                  searchPreferences.updatePreference("searchAlgoVersion", "v3.5");
                }
                userOnboardingStore.reset();
                showDialog = true;
              }}
            />
            <button
              type="submit"
              disabled={isSearchDisabled}
              class={cn(
                "bg-white dark:bg-theme-background-primary",
                "text-black dark:text-theme-gray-600",
                "-mr-2 h-9 max-w-9 rounded-full md:h-7 md:max-w-7",
                "m-0 flex w-full items-center justify-center overflow-hidden p-0",
                "hover:bg-white/90 dark:hover:bg-theme-background-secondary",
                "transition-colors duration-150",
                showSendButton ? "" : "hidden",
              )}
            >
              {@html arrowUpIcon}
            </button>
          </div>

          <div
            class={cn(
              "transition-default flex items-center justify-between px-4",
              showStatusBar ? "py-0" : "h-0 overflow-hidden py-0",
            )}
          >
            <span class="text-xs">
              {searchesRemainingStatus}
            </span>
            <span class="pb-[3px]">
              <a
                href={routes.pricing}
                class="transition-default text-xs text-theme-text-secondary underline hover:text-theme-text-primary"
                onclick={(e) => {
                  e.preventDefault();
                  showDialog = true;
                }}
              >
                Upgrade plan
              </a>
            </span>
          </div>

          {#if activePill && currentSuggestions.length > 0}
            <div class="absolute z-10 mt-2 w-full" class:invisible={!isShowingSuggestions}>
              <div class="py-2">
                {#each currentSuggestions as suggestion}
                  <button
                    onclick={() => handleSuggestionClick(suggestion)}
                    disabled={isSearchDisabled}
                    class="hover:bg-theme-accent hover:bg-theme-accent-hover group w-full px-4 py-2 text-left transition-colors"
                    class:opacity-50={isSearchDisabled}
                  >
                    <div class="flex items-center">
                      <span class="mr-2 text-theme-text-primary transition-colors">
                        <!-- {@html arrowNewlineIcon} -->
                        <CornerDownRight size="16" class="text-theme-text-primary transition-colors" />
                      </span>
                      <span class="transition-default text-sm font-normal text-theme-text-primary hover:font-medium"
                        >{suggestion}</span
                      >
                    </div>
                  </button>
                {/each}
              </div>
            </div>
          {/if}
        </div>
      </div>
    </form>

    {#if showSuggestions}
      <div class={cn("mt-6", activePill ? "invisible" : "visible")}>
        <div
          class="flex w-full flex-wrap items-center justify-center gap-4 opacity-100 transition-opacity duration-200 ease-out sm:gap-3"
        >
          {#each pills as pill}
            <button
              onclick={() => handlePillClick(pill)}
              class="hover:bg-theme-accent hover:bg-theme-accent-hover flex min-w-fit items-center gap-2 whitespace-nowrap rounded-base border border-theme-text-primary bg-theme-background-primary px-3 py-1 text-sm text-theme-text-primary transition-colors dark:border-theme-text-secondary dark:bg-theme-background-secondary dark:text-theme-text-secondary dark:hover:border-theme-text-primary dark:hover:text-theme-text-primary"
            >
              <span>{pill.emoji}</span>
              <span>{pill.text}</span>
            </button>
          {/each}
        </div>
      </div>
    {/if}
  </div>
</div>

<SubscriptionDialog bind:open={showDialog} />

<style>
  @media (max-width: 767px) {
    .text-left {
      width: 100%;
      display: flex;
      flex-direction: column;
    }

    .suggestions-wrapper {
      order: 2;
      margin-top: 0;
      margin-bottom: 1rem;
    }

    form {
      order: 3;
      margin-top: auto;
      padding-bottom: 1rem;
    }
  }
</style>
